import { useCallback, useEffect, useState } from "react";

import type {
  OfferLandingPages,
  OfferProgram,
  RebateProgramOption,
  ReferralLinkExisting,
  ReferralLinkModalsActions,
} from "./types";

type Params = {
  isOpen: boolean;
  selectedHOID: string | undefined;
  actions: ReferralLinkModalsActions;
};

const compareRebatePrograms = (a: OfferProgram, b: OfferProgram) =>
  a.hoid.localeCompare(b.hoid, undefined, { numeric: true }) ||
  a.currency.localeCompare(b.currency) ||
  a.rebateAccount.localeCompare(b.rebateAccount, undefined, {
    numeric: true,
  });

export const useCreateReferralLinkData = ({
  isOpen,
  selectedHOID,
  actions,
}: Params) => {
  const [offerPrograms, setOfferPrograms] = useState<OfferProgram[]>([]);
  const [isGettingOfferIds, setIsGettingOfferIds] = useState(true);

  const [existingReferralLinks, setExistingReferralLinks] = useState<
    ReferralLinkExisting[]
  >([]);
  const [isGettingReferralLinks, setIsGettingReferralLinks] = useState(true);

  const [landingPageOffers, setLandingPageOffers] = useState<
    OfferLandingPages[]
  >([]);
  const [isGettingLandingPage, setIsGettingLandingPage] = useState(false);

  // Track load failures separately so any failed step (offer ids, existing
  // links, or landing pages) surfaces the modal's error state, and so each can
  // be cleared independently when retried.
  const [initialLoadFailed, setInitialLoadFailed] = useState(false);
  const [landingPageFailed, setLandingPageFailed] = useState(false);

  const { fetchUserOfferIds, fetchAllReferralLinks, fetchLandingPages } =
    actions;

  // Load the data the modal needs to open: user offer ids + existing referral
  // links. Exposed via `refetchInitialData` so the error state's refresh button
  // can retry.
  const loadInitialData = useCallback(async () => {
    setInitialLoadFailed(false);
    setIsGettingOfferIds(true);
    setIsGettingReferralLinks(true);

    const loadOfferIds = async () => {
      try {
        const programs = await fetchUserOfferIds();
        setOfferPrograms(programs ?? []);
        return true;
      } catch {
        setOfferPrograms([]);
        return false;
      } finally {
        setIsGettingOfferIds(false);
      }
    };

    const loadReferralLinks = async () => {
      try {
        const links = await fetchAllReferralLinks();
        setExistingReferralLinks(links ?? []);
        return true;
      } catch {
        setExistingReferralLinks([]);
        return false;
      } finally {
        setIsGettingReferralLinks(false);
      }
    };

    const [offersOk, linksOk] = await Promise.all([
      loadOfferIds(),
      loadReferralLinks(),
    ]);
    if (!offersOk || !linksOk) setInitialLoadFailed(true);
  }, [fetchUserOfferIds, fetchAllReferralLinks]);

  // Load the landing pages for a given rebate program (HOID).
  const loadLandingPages = useCallback(
    async (hoid: string) => {
      setLandingPageFailed(false);
      setIsGettingLandingPage(true);
      try {
        const data = await fetchLandingPages(hoid);
        const offers = (data.offers ?? []).filter(
          (o) => o.landingPages && o.landingPages.length > 0,
        );
        setLandingPageOffers(offers);
      } catch {
        setLandingPageOffers([]);
        setLandingPageFailed(true);
      } finally {
        setIsGettingLandingPage(false);
      }
    },
    [fetchLandingPages],
  );

  // Fetch user offer ids + existing referral links when modal opens.
  useEffect(() => {
    if (!isOpen) return;
    void loadInitialData();
  }, [isOpen, loadInitialData]);

  // Fetch landing pages when HOID changes.
  useEffect(() => {
    if (!selectedHOID) {
      setLandingPageOffers([]);
      setLandingPageFailed(false);
      return;
    }

    void loadLandingPages(selectedHOID);
  }, [selectedHOID, loadLandingPages]);

  // Retry every step the modal needs to open. The landing pages effect won't
  // re-run on its own (HOID is unchanged), so reload them explicitly here.
  const refetchInitialData = useCallback(async () => {
    await loadInitialData();
    if (selectedHOID) await loadLandingPages(selectedHOID);
  }, [loadInitialData, loadLandingPages, selectedHOID]);

  const hasError = initialLoadFailed || landingPageFailed;

  const refetchReferralLinks = useCallback(async () => {
    try {
      const links = await fetchAllReferralLinks();
      setExistingReferralLinks(links ?? []);
    } catch {
      // silently fall back
    }
  }, [fetchAllReferralLinks]);

  const rebateProgramOptions: RebateProgramOption[] = [...(offerPrograms ?? [])]
    .sort(compareRebatePrograms)
    .map((p) => ({
      label: `HOID ${p.hoid} ⋅ ${p.currency}`,
      value: `${p.hoid}-${p.rebateAccount}-${p.currency}`,
      isMasterSubIB: false,
      hoid: p.hoid,
      rebateAccount: p.rebateAccount,
      currency: p.currency,
    }));

  const defaultRebateProgram =
    rebateProgramOptions.length === 1 ? rebateProgramOptions[0] : undefined;

  return {
    offerIds: offerPrograms.map((p) => p.hoid),
    isGettingOfferIds,
    existingReferralLinks,
    isGettingReferralLinks,
    refetchReferralLinks,
    landingPageOffers,
    isGettingLandingPage,
    rebateProgramOptions,
    defaultRebateProgram,
    hasError,
    refetchInitialData,
  };
};
